/**
 * R268gg · Feb 2026 — GST Sales Composer (create + edit).
 *
 * Extracted from `GstSales.jsx`. Dual-purpose:
 *   • Create a fresh estimate/SO/invoice/vendor-bill/credit-note/etc.
 *   • Edit an existing doc when `editDoc` is passed. Post-finalized
 *     edits require a change reason (R268r · revision audit log).
 *
 * Live GST/TDS math re-computes on every keystroke via
 * `POST /api/v1/gst/compute` (no backend persist). Multi-currency
 * billing follows R268w: foreign docs capture a `fx_rate` and
 * derive `grand_total_home` server-side.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  CheckCircle2, X, ShieldCheck, Repeat,
  User, Calendar, ListChecks, Percent, StickyNote, GitBranch, Paperclip, Landmark,
  ChevronRight, ChevronDown,
} from "lucide-react";
import SearchableSelect from "@/components/SearchableSelect";
import SmartRichText from "@/components/SmartRichText";
import { CURRENCIES } from "@/lib/currency";
import { EMPTY_LINE, StatusPill } from "./shared";
import CustomerHeader from "./CustomerHeader";
import LineItemGrid from "./LineItemGrid";
import TotalsPanel from "./TotalsPanel";
import EstimatePicker from "./EstimatePicker";
import CatalogDialog from "./CatalogDialog";
import AttachmentsPanel from "./AttachmentsPanel";

// Feb 2026 R293u · UI-only rename. `doc_type == "estimate"` in the DB
// but every user-facing label reads "Quotation" so there's no clash
// with the standalone (internal cost sheet) Estimates module.
const _DOC_TYPE_LABEL = {
  estimate: "quotation",
  sales_order: "sales order",
  invoice: "invoice",
  credit_note: "credit note",
  vendor_bill: "vendor bill",
  debit_note: "debit note",
};
const _label = (t) => _DOC_TYPE_LABEL[t] || (t || "").replace("_", " ");
const _COVER_TITLE = {
  estimate: "QUOTATION",
  sales_order: "SALES ORDER",
  invoice: "TAX INVOICE",
  credit_note: "CREDIT NOTE",
  vendor_bill: "VENDOR BILL",
  debit_note: "DEBIT NOTE",
};

// Feb 2026 R293z · Mini A4-ish thumbnail of what the cover page will
// look like. Not pixel-perfect vs. the backend PDF — it's a visual
// hint so the user knows if the accent and tagline read well before
// hitting Save. Scales to its container width.
function CoverThumb({ accent, tagline, docType, subject, customer, docNo, docDate, wsName }) {
  const title = _COVER_TITLE[docType] || "DOCUMENT";
  const ar = { aspectRatio: "210 / 297" }; // A4
  return (
    <div
      className="border border-border rounded shadow-sm bg-white dark:bg-neutral-50 relative overflow-hidden"
      style={ar}
      data-testid="gst-composer-cover-preview"
    >
      {/* Top accent banner */}
      <div style={{ background: accent, height: "9%" }} />
      {/* Body */}
      <div className="px-[6%] pt-[6%] pb-[4%] flex flex-col h-[91%] text-neutral-900">
        <div
          className="font-bold leading-tight"
          style={{ color: accent, fontSize: "clamp(14px, 2.6cqw, 32px)" }}
        >
          {title}
        </div>
        {tagline && (
          <div className="italic text-neutral-500 mt-[2%]" style={{ fontSize: "clamp(7px, 1.1cqw, 12px)" }}>
            {tagline}
          </div>
        )}
        <div className="mt-[12%]">
          {subject && (
            <>
              <div className="uppercase tracking-wider text-neutral-500" style={{ fontSize: "clamp(6px, 0.85cqw, 10px)" }}>Subject</div>
              <div className="font-semibold text-neutral-800 mt-[1%] truncate" style={{ fontSize: "clamp(8px, 1.3cqw, 14px)" }}>
                {subject}
              </div>
            </>
          )}
        </div>
        <div className="mt-[6%]">
          <div className="uppercase tracking-wider text-neutral-500" style={{ fontSize: "clamp(6px, 0.85cqw, 10px)" }}>Prepared for</div>
          <div className="font-semibold text-neutral-800 mt-[1%] truncate" style={{ fontSize: "clamp(8px, 1.3cqw, 14px)" }}>
            {customer || "—"}
          </div>
        </div>
        <div className="mt-[4%] text-neutral-500" style={{ fontSize: "clamp(6px, 0.95cqw, 11px)" }}>
          <b>No.</b> {docNo || "QUO-XXXX"} &nbsp;·&nbsp; <b>Date</b> {docDate || ""}
        </div>
        <div className="flex-1" />
        <div className="border-t border-neutral-200 pt-[3%] font-semibold text-neutral-700 truncate" style={{ fontSize: "clamp(7px, 1.1cqw, 12px)" }}>
          {wsName || "Your Workspace"}
        </div>
      </div>
    </div>
  );
}

// Feb 2026 R293u · Section card for the Composer. Keeps every form
// block visually grouped with an icon + title + helper hint so the
// dialog scans well on a phone screen (one card = one tap target
// area). Keep the markup light — no shadows, no excessive padding.
// Feb 2026 R293zp · Compacted — smaller icon, tighter padding, inline
// hint on wider screens so cards take ~30% less vertical real estate.
// Feb 2026 · Collapsible — opt-in via `collapsible` + `defaultOpen`.
// Keeps secondary sections (PDF package, Attachments, Notes, Bank,
// Recurring) tucked away so the composer scans as a single screen.
function Section({ icon: Icon, title, hint, children, testid,
                   collapsible = false, defaultOpen = true, badge = null }) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = collapsible ? open : true;
  const headerCls = "flex items-start gap-2" + (collapsible ? " cursor-pointer select-none" : "");
  return (
    <section
      className="border border-border rounded-lg bg-card p-2 sm:p-2.5 space-y-1.5"
      data-testid={testid}
    >
      <div
        className={headerCls}
        onClick={collapsible ? () => setOpen((v) => !v) : undefined}
        role={collapsible ? "button" : undefined}
        tabIndex={collapsible ? 0 : undefined}
        onKeyDown={collapsible ? (e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((v) => !v); }
        } : undefined}
        data-testid={collapsible ? `${testid}-toggle` : undefined}
      >
        <div className="h-6 w-6 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Icon className="h-3 w-3" />
        </div>
        <div className="min-w-0 flex-1 flex flex-wrap items-baseline gap-x-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-foreground">{title}</div>
          {hint && <div className="text-[10px] text-muted-foreground leading-snug">{hint}</div>}
        </div>
        {badge && (
          <div className="shrink-0 self-center">{badge}</div>
        )}
        {collapsible && (
          <div className="shrink-0 self-center text-muted-foreground">
            {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </div>
        )}
      </div>
      {isOpen && <div className="space-y-2">{children}</div>}
    </section>
  );
}

export default function Composer({ open, onClose, docType, ws, onCreated, editDoc, profile }) {
  // R268r · Feb 2026 — Composer is dual-purpose now:
  // creating a fresh doc, or editing an existing one (`editDoc` present).
  const isEdit = !!editDoc;
  // R268v · Feb 2026 — Non-GST workspaces suppress GSTIN/State/POS/HSN/
  // GST% columns and TDS. Preview title also loses the "TAX" prefix.
  const gstOn = profile?.gst_registered !== false;
  const [customer, setCustomer] = useState({
    name: "", gstin: "", state_code: "", billing_address: "",
  });
  const [supplierState, setSupplierState] = useState("27");
  // R268w · Feb 2026 — Doc currency + FX. Defaults to workspace home
  // currency (INR unless the org set otherwise).
  const homeCurrency = profile?.home_currency || "INR";
  const [currency, setCurrency] = useState(homeCurrency);
  const [fxRate, setFxRate] = useState(1);
  const [placeOfSupply, setPlaceOfSupply] = useState("");
  const [taxInclusive, setTaxInclusive] = useState(false);
  const [reverseCharge, setReverseCharge] = useState(false);
  const [tdsSection, setTdsSection] = useState("");
  const [tdsRate, setTdsRate] = useState(0);
  const [additionalCharges, setAdditionalCharges] = useState(0);
  const [notes, setNotes] = useState("");
  // Feb 2026 R293zzb · Per-doc bank-account override. "" or null means
  // "let the renderer auto-pick by currency / default". A real id
  // forces that account onto this doc only.
  const [bankAccountId, setBankAccountId] = useState("");
  // Feb 2026 R293zze · Per-doc payment instructions (free text).
  const [paymentInstructions, setPaymentInstructions] = useState("");
  // Feb 2026 R293v · Short "what's this doc for?" line. Shown in the
  // list view below the doc_no and on the PDF between the header and
  // the parties block so the customer sees it too.
  const [subject, setSubject] = useState("");
  // Feb 2026 R293w · Branded-PDF per-doc overrides. `null` means
  // "fall through to the workspace billing-profile default". The
  // composer surfaces three independent toggles so a user can turn
  // the cover on without the terms, or vice-versa.
  const [includeCover, setIncludeCover] = useState(null);
  const [includeDetails, setIncludeDetails] = useState(null);
  const [includeTerms, setIncludeTerms] = useState(null);
  const [detailsOverride, setDetailsOverride] = useState("");
  const [termsOverride, setTermsOverride] = useState("");
  // Feb 2026 · Per-doc cover tagline override. Falls back to the
  // workspace billing-profile tagline when blank. Lets a user pitch a
  // bespoke punchline on a hero quotation without touching defaults.
  const [coverTagline, setCoverTagline] = useState("");
  // Feb 2026 R293z · Billing profile snapshot — powers the cover
  // preview thumbnail (accent + tagline + legal name defaults).
  const [bp, setBp] = useState(null);
  // R268r · Free-text audit note captured when editing a finalized doc.
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  // Feb 2026 R286 · Editable document date. Defaults to today for New,
  // hydrates from the source doc for Edit. Also updates due_date when
  // changed on Edit if the user hasn't overridden it separately.
  const [docDate, setDocDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [states, setStates] = useState([]);
  const [tdsCatalog, setTdsCatalog] = useState([]);
  const [gstinCheck, setGstinCheck] = useState(null);
  const [hsn, setHsn] = useState([]);
  // R268s · Feb 2026 — clients list drives the searchable Customer combobox.
  const [clients, setClients] = useState([]);
  // R268u · Feb 2026 — when true, after posting the doc we also POST
  // to /clients so the customer becomes reusable in future compositions.
  const [saveNewClient, setSaveNewClient] = useState(false);
  // R268r · Free-text audit note captured when editing a finalized doc.
  const [revisionNote, setRevisionNote] = useState("");
  // Recurring bits (only shown for invoice type, and only on create)
  const [recurringOn, setRecurringOn] = useState(false);
  const [recurringName, setRecurringName] = useState("");
  const [recurringCadence, setRecurringCadence] = useState("monthly");
  const [recurringStart, setRecurringStart] = useState(
    () => new Date().toISOString().slice(0, 10));
  // Feb 2026 R293zh · Standalone-Estimate link state. The picker only
  // opens on the composer's Line-items section; when the user picks an
  // approved estimate we fetch its reshaped lines and seed the grid
  // (and prefill customer + subject when the picker reports them).
  const [estimatePickerOpen, setEstimatePickerOpen] = useState(false);
  const [sourceEstimateId, setSourceEstimateId] = useState(null);
  const [sourceEstimateNumber, setSourceEstimateNumber] = useState(null);
  // Feb 2026 R293zo · Catalog picker (multi-select) from the line
  // items block. When items are picked they're appended as fresh
  // lines prefilled with HSN/SAC, description, GST rate; qty = 1.
  const [catalogOpen, setCatalogOpen] = useState(false);

  // Reset composer whenever it opens
  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      // Hydrate every field from the incoming document.
      setCustomer({
        name: editDoc.customer_name || "",
        gstin: editDoc.customer_gstin || "",
        state_code: editDoc.customer_state_code || "",
        billing_address: editDoc.customer_billing_address || "",
      });
      setSupplierState(editDoc.supplier_state_code || "27");
      setCurrency(editDoc.currency || editDoc.totals?.currency || homeCurrency);
      setFxRate(Number(editDoc.fx_rate || editDoc.totals?.fx_rate || 1));
      setPlaceOfSupply(editDoc.place_of_supply_code || "");
      setTaxInclusive(!!editDoc.tax_inclusive);
      setReverseCharge(!!editDoc.reverse_charge);
      setTdsSection(editDoc.tds_section || "");
      setTdsRate(editDoc.tds_rate || 0);
      setAdditionalCharges(editDoc.additional_charges || 0);
      setNotes(editDoc.notes || "");
      setBankAccountId(editDoc.bank_account_id || "");
      setPaymentInstructions(editDoc.payment_instructions || "");
      setSubject(editDoc.subject || "");
      setIncludeCover(editDoc.include_cover ?? null);
      setIncludeDetails(editDoc.include_details ?? null);
      setIncludeTerms(editDoc.include_terms ?? null);
      setDetailsOverride(editDoc.details_override || "");
      setTermsOverride(editDoc.terms_override || "");
      setCoverTagline(editDoc.custom_cover_tagline || "");
      setDocDate(editDoc.doc_date || new Date().toISOString().slice(0, 10));
      setDueDate(editDoc.due_date || "");
      // Strip out computed columns (taxable/gst_amount/line_total) so
      // the calculator recomputes them from source values on every edit.
      setLines(
        (editDoc.lines || [{ ...EMPTY_LINE }]).map((l) => ({
          hsn_sac: l.hsn_sac || "",
          description: l.description || "",
          unit: l.unit || "Nos",
          qty: l.qty ?? 1,
          rate: l.rate ?? 0,
          discount_pct: l.discount_pct ?? 0,
          gst_rate: l.gst_rate ?? 18,
          cess_rate: l.cess_rate ?? 0,
        })),
      );
      setRevisionNote("");
      setSaveNewClient(false);
      setSourceEstimateId(editDoc.source_estimate_id || null);
      setSourceEstimateNumber(editDoc.source_estimate_number || null);
    } else {
      setCustomer({ name: "", gstin: "", state_code: "", billing_address: "" });
      setSupplierState("27");
      setCurrency(homeCurrency);
      setFxRate(1);
      setPlaceOfSupply("");
      setTaxInclusive(false);
      setReverseCharge(false);
      setTdsSection("");
      setTdsRate(0);
      setAdditionalCharges(0);
      setNotes("");
      setSubject("");
      setPaymentInstructions("");
      setIncludeCover(null);
      setIncludeDetails(null);
      setIncludeTerms(null);
      setDetailsOverride("");
      setTermsOverride("");
      setCoverTagline("");
      setLines([{ ...EMPTY_LINE }]);
      setDocDate(new Date().toISOString().slice(0, 10));
      setDueDate("");
      setRevisionNote("");
      setSaveNewClient(false);
      setSourceEstimateId(null);
      setSourceEstimateNumber(null);
    }
    setPreview(null);
    setGstinCheck(null);
    setRecurringOn(false);
    setRecurringName("");
    setRecurringCadence("monthly");
    setRecurringStart(new Date().toISOString().slice(0, 10));
  }, [open, isEdit, editDoc]);

  // Load reference data once
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [s, t, h, c, b] = await Promise.all([
          api.get("/gst/states"),
          api.get("/gst/tds-sections"),
          ws ? api.get(`/gst/workspaces/${ws}/hsn`) : Promise.resolve({ data: { items: [] } }),
          // R268s · workspace clients — used for the searchable Customer combobox.
          ws ? api.get(`/workspaces/${ws}/clients`) : Promise.resolve({ data: [] }),
          // R293z · billing profile — powers the cover preview.
          ws ? api.get(`/gst/workspaces/${ws}/billing-profile`).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        ]);
        setStates(s.data.states || []);
        setTdsCatalog(t.data.sections || []);
        setHsn(h.data.items || []);
        // Endpoint returns either a raw array or {clients:[...]}.
        const rawClients = Array.isArray(c.data) ? c.data
                            : (c.data?.clients || c.data?.items || []);
        setClients(rawClients);
        setBp(b.data || {});
      } catch (e) { toast.error(apiErrorMessage(e)); }
    })();
  }, [open, ws]);

  const doCompute = useCallback(async () => {
    try {
      const r = await api.post("/gst/compute", {
        supplier_state_code: supplierState,
        place_of_supply_code: placeOfSupply || customer.state_code || supplierState,
        tax_inclusive: taxInclusive,
        tds_rate: Number(tdsRate) || 0,
        additional_charges: Number(additionalCharges) || 0,
        lines: lines.map((l) => ({
          ...l, qty: Number(l.qty) || 0, rate: Number(l.rate) || 0,
          discount_pct: Number(l.discount_pct) || 0,
          gst_rate: Number(l.gst_rate) || 0,
          cess_rate: Number(l.cess_rate) || 0,
        })),
      });
      setPreview(r.data);
    } catch (e) { /* silent — user typing */ }
  }, [supplierState, placeOfSupply, customer.state_code, taxInclusive,
      tdsRate, additionalCharges, lines]);

  // Debounced live preview
  useEffect(() => {
    if (!open) return;
    const h = setTimeout(doCompute, 300);
    return () => clearTimeout(h);
  }, [doCompute, open]);

  // GSTIN reverse lookup (auto-fills state). Stable for CustomerHeader memo.
  const checkGstin = useCallback(async (gstin) => {
    setCustomer((c) => ({ ...c, gstin }));
    if (!gstin || gstin.length < 15) { setGstinCheck(null); return; }
    try {
      const r = await api.post("/gst/validate-gstin", { gstin });
      setGstinCheck(r.data);
      if (r.data.valid && r.data.state_code) {
        setCustomer((c) => ({ ...c, state_code: r.data.state_code }));
      }
    } catch (e) { /* ignore */ }
  }, []);

  // R268hh · Feb 2026 — Stable callbacks so the memoised LineItemGrid
  // + LineRow only re-render when the row they actually own changes.
  // Without useCallback here, every keystroke recreates these fns
  // and defeats React.memo — mobile edits stay >500ms on long lists.
  const setLine = useCallback((i, patch) =>
    setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l)), []);
  const rmLine = useCallback((i) => setLines((prev) => prev.filter((_, idx) => idx !== i)), []);
  const addLine = useCallback(() => setLines((prev) => [...prev, { ...EMPTY_LINE }]), []);
  // Feb 2026 R293zq · Append catalog picks as fresh lines, prefilled
  // from the Item Catalog (`/workspaces/{ws}/items`). Each pick already
  // carries a user-chosen qty from the picker dialog.
  // Feb 2026 R293zv · Queued catalog item IDs with specs → copied as
  // attachments into this doc as soon as a doc_id exists. For edits we
  // flush right after a pick; for new docs we flush right after the
  // first save (see save() below).
  const [pendingSpecItemIds, setPendingSpecItemIds] = useState([]);
  const docIdForSpecs = isEdit ? editDoc?.id : null;

  const flushItemSpecs = useCallback(async (ids, didForCopy) => {
    if (!ids?.length || !didForCopy || !ws) return 0;
    let copied = 0;
    for (const iid of ids) {
      try {
        const r = await api.post(
          `/gst/workspaces/${ws}/documents/${didForCopy}/attachments/from-item/${iid}`);
        copied += (r.data?.copied || 0);
      } catch (err) {
        // Soft-fail — the pick itself already succeeded; the user can
        // retry via the attachment panel. Keep the composer unblocked.
        console.warn("spec copy failed for item", iid, err?.message);
      }
    }
    return copied;
  }, [ws]);

  const addLinesFromCatalog = useCallback((picks) => {
    if (!Array.isArray(picks) || !picks.length) return;
    const newLines = picks.map((p) => {
      const desc = [p.name, p.description].filter(Boolean).join(" — ");
      return {
        ...EMPTY_LINE,
        hsn_sac: p.hsn_sac || "",
        description: desc || p.name || "",
        unit: p.unit || "Nos",
        gst_rate: p.tax_pct ?? 18,
        qty: Number(p.qty) || 1,
        rate: Number(p.rate) || 0,
      };
    });
    setLines((prev) => {
      // Drop a single leading blank-first row so first pick replaces it.
      if (prev.length === 1 && !prev[0].description && !prev[0].hsn_sac) {
        return newLines;
      }
      return [...prev, ...newLines];
    });
    const specIds = picks
      .filter((p) => Array.isArray(p.spec_attachments) && p.spec_attachments.length > 0)
      .map((p) => p.id)
      .filter(Boolean);
    const specCount = specIds.reduce(
      (acc, iid) => acc + (picks.find((p) => p.id === iid)?.spec_attachments?.length || 0),
      0);
    if (docIdForSpecs && specIds.length) {
      flushItemSpecs(specIds, docIdForSpecs).then((copied) => {
        if (copied) toast.success(`+${copied} spec sheet${copied === 1 ? "" : "s"} attached from catalog`);
      });
    } else if (specIds.length) {
      setPendingSpecItemIds((prev) => Array.from(new Set([...prev, ...specIds])));
    }
    const label = picks.length === 1 ? "" : "s";
    toast.success(
      `${picks.length} item${label} added from catalog` +
      (specCount && !docIdForSpecs ? ` · ${specCount} spec file${specCount === 1 ? "" : "s"} will attach on save` : ""));
  }, [docIdForSpecs, flushItemSpecs]);
  const pickHsn = useCallback((i, code) => {
    const row = hsn.find((h) => h.code === code);
    setLine(i, {
      hsn_sac: code,
      description: row?.description || "",
      gst_rate: row?.gst_rate ?? 18,
    });
  }, [hsn, setLine]);

  // Feb 2026 R293zq / R293zv · Hydrate a single line row from a catalog
  // item + optionally pull its spec attachments. Qty is preserved so
  // repeating the pick on the same line only overwrites rate/HSN/tax.
  const pickItemIntoLine = useCallback((i, item) => {
    const desc = [item.name, item.description].filter(Boolean).join(" — ");
    setLine(i, {
      description: desc || item.name || "",
      hsn_sac: item.hsn_sac || "",
      unit: item.unit || "Nos",
      gst_rate: item.tax_pct ?? 18,
      rate: Number(item.rate) || 0,
    });
    const specCount = Array.isArray(item.spec_attachments) ? item.spec_attachments.length : 0;
    if (specCount && docIdForSpecs) {
      flushItemSpecs([item.id], docIdForSpecs).then((copied) => {
        if (copied) toast.success(`+${copied} spec sheet${copied === 1 ? "" : "s"} attached from "${item.name}"`);
      });
    } else if (specCount && item.id) {
      setPendingSpecItemIds((prev) => Array.from(new Set([...prev, item.id])));
    }
    toast.success(
      `Line hydrated from ${item.name}` +
      (specCount && !docIdForSpecs ? ` · ${specCount} spec file${specCount === 1 ? "" : "s"} will attach on save` : ""));
  }, [setLine, docIdForSpecs, flushItemSpecs]);

  const onTdsChange = (code) => {
    if (code === "__none__") { setTdsSection(""); setTdsRate(0); return; }
    setTdsSection(code);
    const row = tdsCatalog.find((r) => r.code === code);
    setTdsRate(row?.rate || 0);
  };

  const save = async () => {
    if (!customer.name) { toast.error("Customer name is required"); return; }
    if (!lines.length || !lines[0].description) { toast.error("Add at least one line"); return; }
    setSaving(true);
    const normalisedLines = lines.map((l) => ({
      ...l, qty: Number(l.qty) || 0, rate: Number(l.rate) || 0,
      discount_pct: Number(l.discount_pct) || 0,
      gst_rate: gstOn ? (Number(l.gst_rate) || 0) : 0,
      cess_rate: gstOn ? (Number(l.cess_rate) || 0) : 0,
    }));
    try {
      if (isEdit) {
        await api.patch(
          `/gst/workspaces/${ws}/documents/${editDoc.id}`,
          {
            doc_date: docDate || editDoc.doc_date,
            due_date: dueDate || null,
            customer_name: customer.name,
            customer_gstin: customer.gstin || null,
            customer_state_code: customer.state_code || null,
            customer_billing_address: customer.billing_address || null,
            place_of_supply_code: placeOfSupply || null,
            tax_inclusive: taxInclusive,
            reverse_charge: reverseCharge,
            tds_section: tdsSection || null,
            tds_rate: Number(tdsRate) || 0,
            additional_charges: Number(additionalCharges) || 0,
            notes: notes || null,
            subject: subject || null,
            include_cover: includeCover,
            include_details: includeDetails,
            include_terms: includeTerms,
            details_override: detailsOverride || null,
            terms_override: termsOverride || null,
            custom_cover_tagline: coverTagline || null,
            bank_account_id: bankAccountId || null,
            payment_instructions: paymentInstructions || null,
            lines: normalisedLines,
            currency,
            fx_rate: currency === homeCurrency ? 1 : Number(fxRate) || 1,
            revision_note: revisionNote || null,
          },
        );
        toast.success(`${editDoc.doc_no} updated`);
      } else if (recurringOn && docType === "invoice") {
        if (!recurringName) { toast.error("Give the recurring template a name"); setSaving(false); return; }
        await api.post(`/gst/workspaces/${ws}/recurring`, {
          name: recurringName,
          cadence: recurringCadence,
          start_date: recurringStart,
          customer_name: customer.name,
          customer_gstin: customer.gstin || null,
          customer_state_code: customer.state_code || null,
          customer_billing_address: customer.billing_address || null,
          supplier_state_code: supplierState,
          place_of_supply_code: placeOfSupply || null,
          tax_inclusive: taxInclusive,
          reverse_charge: reverseCharge,
          tds_section: tdsSection || null,
          tds_rate: Number(tdsRate) || 0,
          additional_charges: Number(additionalCharges) || 0,
          notes: notes || null,
          bank_account_id: bankAccountId || null,
          payment_instructions: paymentInstructions || null,
          lines: normalisedLines,
        });
        toast.success("Recurring template saved");
      } else {
        // R268u · Feb 2026 — Save the new customer to the Clients
        // module before posting the doc so the record shows up on
        // Clients > Overview. Best-effort — if it fails, the doc
        // still gets created; we surface a non-blocking toast.
        if (saveNewClient && customer.name?.trim()) {
          try {
            const [line1, ...rest] = (customer.billing_address || "").split("\n");
            await api.post(`/workspaces/${ws}/clients`, {
              name: customer.name.trim(),
            });
            // Then patch the CRM-extended fields the composer needs
            // pulled back on selection next time.
            const list = await api.get(`/workspaces/${ws}/clients`);
            const newRow = (Array.isArray(list.data) ? list.data
                              : (list.data.clients || list.data.items || []))
              .find((c) => (c.name || "").toLowerCase() === customer.name.trim().toLowerCase());
            if (newRow?.id) {
              await api.patch(`/clients/${newRow.id}`, {
                legal_name: customer.name.trim(),
                tax_id_label: customer.gstin ? "GSTIN" : null,
                tax_id_value: customer.gstin || null,
                billing_address_line1: line1 || null,
                billing_address_line2: rest.join("\n") || null,
              });
            }
            toast.success(`Saved ${customer.name.trim()} to Clients`);
          } catch (e) {
            // Non-blocking — don't stop the doc creation.
            toast.warning(`Couldn't save client: ${apiErrorMessage(e)}`);
          }
        }
        const createRes = await api.post(`/gst/workspaces/${ws}/documents`, {
          doc_type: docType,
          doc_date: docDate || new Date().toISOString().slice(0, 10),
          due_date: dueDate || null,
          customer_name: customer.name,
          customer_gstin: customer.gstin || null,
          customer_state_code: customer.state_code || null,
          customer_billing_address: customer.billing_address || null,
          supplier_state_code: supplierState,
          place_of_supply_code: placeOfSupply || null,
          tax_inclusive: taxInclusive,
          reverse_charge: reverseCharge,
          tds_section: tdsSection || null,
          tds_rate: Number(tdsRate) || 0,
          additional_charges: Number(additionalCharges) || 0,
          notes: notes || null,
          subject: subject || null,
          include_cover: includeCover,
          include_details: includeDetails,
          include_terms: includeTerms,
          details_override: detailsOverride || null,
          terms_override: termsOverride || null,
          custom_cover_tagline: coverTagline || null,
          lines: normalisedLines,
          currency,
          fx_rate: currency === homeCurrency ? 1 : Number(fxRate) || 1,
          bank_account_id: bankAccountId || null,
          payment_instructions: paymentInstructions || null,
          source_estimate_id: sourceEstimateId || null,
        });
        toast.success(`${_label(docType)} created`);
        // Feb 2026 R293zv · Flush queued catalog spec sheets into the
        // freshly-created doc. Non-blocking — the base save already
        // succeeded; attachments ride in on a best-effort tick.
        const newDid = createRes.data?.id;
        if (newDid && pendingSpecItemIds.length) {
          flushItemSpecs(pendingSpecItemIds, newDid).then((copied) => {
            if (copied) toast.success(`+${copied} spec sheet${copied === 1 ? "" : "s"} attached from catalog`);
            setPendingSpecItemIds([]);
          });
        }
      }
      onCreated?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="gst-composer"
        onKeyDown={(e) => {
          // R268ii · Feb 2026 — Cmd/Ctrl+Enter saves from anywhere
          // inside the composer (skip when a Select is capturing keys).
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && !saving) {
            e.preventDefault();
            save();
          }
        }}
        className="p-0 gap-0 w-screen max-w-full h-[100dvh] max-h-[100dvh]
                     rounded-none border-0 flex flex-col
                     sm:max-w-[min(1400px,96vw)] sm:h-auto sm:max-h-[94vh] sm:rounded-xl sm:my-3
                     data-[state=open]:duration-200"
      >
        <div className="sticky top-0 z-10 bg-background border-b border-border px-3 sm:px-4 py-2 flex items-start justify-between gap-2 shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <DialogTitle className="text-sm sm:text-base capitalize truncate" data-testid="gst-composer-title">
                {isEdit ? `Edit ${editDoc.doc_no}` : `New ${_label(docType)}`}
              </DialogTitle>
              {/* Feb 2026 R293u · Prominent status pill in the header
                  so users see at a glance whether this doc is live or
                  still a draft. Version chip on estimates w/ ≥ v2. */}
              {isEdit && editDoc.status && (
                <StatusPill status={editDoc.status} />
              )}
              {isEdit && editDoc.doc_type === "estimate" && (editDoc.version || 1) > 1 && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide
                                   text-violet-800 dark:text-violet-200 bg-violet-100 dark:bg-violet-950/60
                                   border border-violet-300 dark:border-violet-800 rounded px-1.5 py-0.5"
                         data-testid="gst-composer-version"
                         title={`Version ${editDoc.version} of this estimate`}>
                  <GitBranch className="h-2.5 w-2.5" /> v{editDoc.version}
                </span>
              )}
            </div>
            <DialogDescription className="text-[11px] sm:text-xs mt-0.5">
              {isEdit ? (
                <>
                  {editDoc.status && !["draft"].includes(editDoc.status) && (
                    <span className="text-amber-700 dark:text-amber-300 font-medium">
                      Every change is logged in the revision history.
                    </span>
                  )}
                  {(!editDoc.status || editDoc.status === "draft") &&
                    "Live totals update as you type."}
                </>
              ) : (
                <span className="hidden sm:inline">Live totals update as you type · GST split follows place of supply.</span>
              )}
            </DialogDescription>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <a
              href="/app/settings/billing-profile"
              className="hidden sm:inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground border border-border rounded px-2 py-1"
              data-testid="gst-composer-billing-profile-link"
              target="_blank" rel="noreferrer">
              <ShieldCheck className="h-3 w-3" /> Template
            </a>
            <Button variant="ghost" size="icon" className="h-9 w-9"
                      data-testid="gst-composer-close" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="px-3 sm:px-4 py-2 overflow-y-auto flex-1 min-h-0 bg-muted/20">
          <div className="space-y-2 max-w-[1200px] mx-auto">
          {/* Customer / Vendor */}
          <Section
            icon={User}
            title={docType === "vendor_bill" || docType === "debit_note" ? "Vendor" : "Customer"}
            hint="Who are you billing? GSTIN auto-fills state and tax split."
            testid="gst-composer-section-customer"
          >
            <CustomerHeader
              docType={docType}
              customer={customer}
              setCustomer={setCustomer}
              clients={clients}
              states={states}
            gstinCheck={gstinCheck}
            checkGstin={checkGstin}
            gstOn={gstOn}
            saveNewClient={saveNewClient}
            setSaveNewClient={setSaveNewClient}
            isEdit={isEdit}
          />
          {/* Feb 2026 R293v · Subject line. Shows in list views + PDF
              under the doc_no so the purpose is clear at a glance. */}
          <div className="pt-2 border-t border-dashed border-border">
            <Label className="text-[11px]">Subject</Label>
            <Input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={docType === "estimate" ? "e.g. Website redesign — Phase 1" : "What is this document for?"}
              className="rounded-none h-9 text-sm"
              maxLength={120}
              data-testid="gst-composer-subject"
            />
            <div className="text-[10px] text-muted-foreground mt-0.5">
              Appears below the doc number in the list view and on the PDF.
            </div>
          </div>
          </Section>

          {/* Dates + Currency — merged for density (R293zp · Feb 2026). */}
          <Section
            icon={Calendar}
            title="Dates & currency"
            hint={currency === homeCurrency
              ? `Due date drives aging. Billing in ${homeCurrency}.`
              : `Due date drives aging. Foreign ${currency} — FX stamped at save.`}
            testid="gst-composer-section-dates"
          >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="gst-composer-dates">
            <div>
              <Label className="text-[11px]">{docType === "invoice" ? "Invoice date" : docType === "estimate" ? "Quotation date" : "Doc date"}</Label>
              <Input
                type="date"
                value={docDate}
                onChange={(e) => setDocDate(e.target.value)}
                className="rounded-none h-9 text-sm"
                data-testid="gst-composer-doc-date"
              />
            </div>
            <div>
              <Label className="text-[11px]">Due date</Label>
              <Input
                type="date"
                value={dueDate}
                min={docDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="rounded-none h-9 text-sm"
                data-testid="gst-composer-due-date"
              />
            </div>
            <div data-testid="gst-composer-currency-section">
              <Label className="text-[11px]">Bill in</Label>
              <SearchableSelect
                value={currency}
                onChange={async (v) => {
                  setCurrency(v);
                  if (v === homeCurrency) { setFxRate(1); return; }
                  try {
                    const r = await api.get(
                      `/gst/fx-rate?from=${v}&to=${homeCurrency}`);
                    if (r?.data?.rate) setFxRate(r.data.rate);
                  } catch (e) { /* fall through — manual entry */ }
                }}
                options={CURRENCIES.map((c) => ({
                  value: c.code,
                  label: `${c.code} · ${c.label}`,
                  hint: c.symbol,
                }))}
                placeholder="Currency"
                searchPlaceholder="Search currency…"
                testid="gst-composer-currency"
                className="h-9"
                preserveOrder
              />
            </div>
            {currency !== homeCurrency ? (
              <div>
                <Label className="text-[11px]">FX ({currency}→{homeCurrency})</Label>
                <Input type="number" inputMode="decimal" step="0.0001" className="h-9 text-sm"
                          data-testid="gst-composer-fx-rate"
                          value={fxRate}
                          onChange={(e) => setFxRate(e.target.value)} />
              </div>
            ) : <div className="hidden sm:block" />}
          </div>
          {isEdit && docDate !== editDoc?.doc_date && (
            <div className="text-[10px] text-amber-700">
              Date changed from {editDoc?.doc_date} — may shift the fiscal-year sequence on next new doc.
            </div>
          )}
          </Section>

          <Section
            icon={ListChecks}
            title="Line items"
            hint="Tap + Add row for each item. Qty × Rate × GST drives the total."
            testid="gst-composer-section-lines"
          >
          {/* Feb 2026 R293zh · Import from standalone Estimate.
              Only on quotations (estimate doc_type). Pill under the
              trigger reminds the user which estimate was seeded. */}
          {docType === "estimate" && !isEdit && (
            <div className="flex items-center justify-between gap-2 flex-wrap">
              {sourceEstimateId ? (
                <div className="inline-flex items-center gap-2 text-[11px] font-mono px-2 py-1 border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 rounded text-emerald-800 dark:text-emerald-200"
                     data-testid="gst-composer-source-estimate-pill">
                  <ShieldCheck className="h-3 w-3" />
                  Linked to Estimate <b>{sourceEstimateNumber}</b>
                  <button
                    type="button"
                    onClick={() => { setSourceEstimateId(null); setSourceEstimateNumber(null); }}
                    className="ml-1 text-[10px] uppercase tracking-wide hover:underline"
                    data-testid="gst-composer-source-estimate-clear"
                  >
                    clear
                  </button>
                </div>
              ) : <span />}
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => setEstimatePickerOpen(true)}
                data-testid="gst-composer-import-estimate"
              >
                <ListChecks className="h-3 w-3 mr-1" /> Import from Estimate
              </Button>
            </div>
          )}
          <LineItemGrid
            lines={lines}
            setLine={setLine}
            addLine={addLine}
            rmLine={rmLine}
            pickHsn={pickHsn}
            hsn={hsn}
            gstOn={gstOn}
            onOpenCatalog={() => setCatalogOpen(true)}
            ws={ws}
            pickItem={pickItemIntoLine}
          />
          </Section>

          {/* Taxes · POS · charges — merged (R293zp · Feb 2026). GST
              orgs get supplier state + POS + TDS + addl charges in one
              grid; non-GST orgs collapse to just additional charges. */}
          <Section
            icon={Percent}
            title={gstOn ? "Taxes · place of supply · charges" : "Additional charges"}
            hint={gstOn ? "CGST+SGST same state · IGST inter-state. TDS is deducted by the customer." : "Any extra flat charges on top of line items."}
            testid="gst-composer-section-charges"
          >
          {gstOn ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="gst-composer-section-pos">
            <div>
              <Label className="text-[11px]">Supplier state</Label>
              <SearchableSelect
                value={supplierState}
                onChange={setSupplierState}
                options={states.map((s) => ({
                  value: s.code, label: `${s.code} · ${s.name}`,
                }))}
                placeholder="Supplier state"
                searchPlaceholder="Search state…"
                testid="gst-composer-supplier-state"
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-[11px]">Place of supply</Label>
              <SearchableSelect
                value={placeOfSupply || ""}
                onChange={setPlaceOfSupply}
                options={states.map((s) => ({
                  value: s.code, label: `${s.code} · ${s.name}`,
                }))}
                placeholder="Same as customer"
                searchPlaceholder="Search state…"
                testid="gst-composer-pos"
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-[11px]">TDS section</Label>
              <SearchableSelect
                value={tdsSection || "__none__"}
                onChange={onTdsChange}
                options={[
                  { value: "__none__", label: "None · 0%" },
                  ...tdsCatalog.map((t) => ({
                    value: t.code,
                    label: `${t.label} · ${t.rate}%`,
                    hint: t.code,
                  })),
                ]}
                placeholder="None"
                searchPlaceholder="Search TDS section…"
                testid="gst-composer-tds-section"
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-[11px]">Additional charges ₹</Label>
              <Input type="number" inputMode="decimal" className="h-9 text-sm" value={additionalCharges}
                      data-testid="gst-composer-addl"
                      onChange={(e) => setAdditionalCharges(e.target.value)} />
            </div>
            <label className="flex items-center gap-2 text-xs h-9 col-span-2 sm:col-span-2 border border-border rounded px-3 bg-background cursor-pointer">
              <input type="checkbox" checked={taxInclusive}
                        className="h-3.5 w-3.5 cursor-pointer"
                        data-testid="gst-composer-tax-inclusive"
                        onChange={(e) => setTaxInclusive(e.target.checked)} />
              Prices are tax-inclusive
            </label>
            {docType === "vendor_bill" && (
              <label className="flex items-center gap-2 text-xs h-9 col-span-2 sm:col-span-2 border border-border rounded px-3 bg-background cursor-pointer">
                <input type="checkbox" checked={reverseCharge}
                          className="h-3.5 w-3.5 cursor-pointer"
                          data-testid="gst-composer-reverse-charge"
                          onChange={(e) => setReverseCharge(e.target.checked)} />
                Reverse charge (RCM)
              </label>
            )}
          </div>
          ) : (
          <div>
            <Label className="text-[11px]">Additional charges ₹</Label>
            <Input type="number" inputMode="decimal" className="h-9 text-sm" value={additionalCharges}
                    data-testid="gst-composer-addl"
                    onChange={(e) => setAdditionalCharges(e.target.value)} />
          </div>
          )}
          </Section>

          {/* Recurring toggle — invoice only, and never during edit */}
          {docType === "invoice" && !isEdit && (
            <Section
              icon={Repeat}
              title="Recurring"
              hint="Turn this invoice into a template that bills on a schedule."
              testid="gst-composer-section-recurring"
              collapsible
              defaultOpen={false}
              badge={recurringOn ? (
                <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200">On</span>
              ) : null}
            >
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={recurringOn}
                          className="h-4 w-4 cursor-pointer"
                          data-testid="gst-composer-recurring"
                          onChange={(e) => setRecurringOn(e.target.checked)} />
                <span>Save as a recurring template</span>
              </label>
              {recurringOn && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input
                    data-testid="gst-composer-recurring-name"
                    placeholder="Template name"
                    className="h-9 text-sm"
                    value={recurringName}
                    onChange={(e) => setRecurringName(e.target.value)} />
                  <Select value={recurringCadence} onValueChange={setRecurringCadence}>
                    <SelectTrigger className="h-9 text-sm" data-testid="gst-composer-recurring-cadence">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="date"
                    data-testid="gst-composer-recurring-start"
                    className="h-9 text-sm"
                    value={recurringStart}
                    onChange={(e) => setRecurringStart(e.target.value)} />
                </div>
              )}
            </Section>
          )}

          {/* Feb 2026 R293w · Branded-PDF per-doc overrides. The three
              `include_*` toggles default to the workspace billing-
              profile preference when left on "Default". Users can
              force on/off for THIS doc. Only shown for quote/invoice;
              credit-notes and vendor bills use the plain layout. */}
          {(docType === "estimate" || docType === "invoice") && (
            <Section
              icon={ShieldCheck}
              title="PDF package"
              hint="Attach a cover page, a details/scope page, and a terms page. Defaults from your billing profile — override per doc."
              testid="gst-composer-section-pdf"
              collapsible
              defaultOpen={false}
            >
              {/* Feb 2026 R293z · Side-by-side: toggles on the left,
                  cover preview on the right. Enlarged the preview to
                  260px so users can actually read the cover before
                  save. On narrow phones the thumbnail stacks below. */}
              <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-4">
                <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { label: "Cover page", value: includeCover, setter: setIncludeCover, testid: "cover" },
                  { label: "Details page", value: includeDetails, setter: setIncludeDetails, testid: "details" },
                  { label: "Terms page", value: includeTerms, setter: setIncludeTerms, testid: "terms" },
                ].map(({ label, value, setter, testid }) => (
                  <div key={testid} className="border border-border rounded px-2 py-2 bg-background">
                    <div className="text-[11px] font-medium text-muted-foreground mb-1">{label}</div>
                    <div className="flex gap-1" data-testid={`gst-composer-pdf-${testid}`}>
                      {[
                        { v: null, l: "Default" },
                        { v: true, l: "On" },
                        { v: false, l: "Off" },
                      ].map(({ v, l }) => (
                        <button
                          key={l}
                          type="button"
                          onClick={() => setter(v)}
                          className={`flex-1 h-8 text-[11px] uppercase tracking-wide border transition-colors ${
                            value === v
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background text-muted-foreground border-border hover:bg-muted"
                          }`}
                          data-testid={`gst-composer-pdf-${testid}-${l.toLowerCase()}`}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {(includeDetails === true || (includeDetails == null && (detailsOverride || "").trim())) && (
                <div>
                  <Label className="text-xs">Details / scope override</Label>
                  <SmartRichText
                    value={detailsOverride}
                    onChange={setDetailsOverride}
                    minRows={5}
                    maxHeight={280}
                    mediaMode="url-only"
                    placeholder="Describe the scope, deliverables, assumptions, timeline…"
                    testid="gst-composer-details-override"
                  />
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    Rich text renders on the PDF — headings, bullets, bold &amp; links all carry through.
                  </div>
                </div>
              )}
              {(() => {
                const _coverKey = docType === "estimate" ? "quote" : "invoice";
                const _effCoverOn = includeCover === null
                  ? !!(bp && bp[`${_coverKey}_cover_enabled`])
                  : !!includeCover;
                if (!_effCoverOn) return null;
                return (
                  <div>
                    <Label className="text-xs">Cover tagline (this doc)</Label>
                    <Input
                      data-testid="gst-composer-cover-tagline"
                      placeholder={(bp && bp[`${_coverKey}_cover_tagline`]) || "Leave blank to use the workspace tagline"}
                      className="rounded-none h-9 text-sm"
                      maxLength={160}
                      value={coverTagline}
                      onChange={(e) => setCoverTagline(e.target.value)}
                    />
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      Shown as the italic sub-line under the cover title. One short line reads best.
                    </div>
                  </div>
                );
              })()}
              {(includeTerms === true || (includeTerms == null && (termsOverride || "").trim())) && (
                <div>
                  <Label className="text-xs">Terms override</Label>
                  <SmartRichText
                    value={termsOverride}
                    onChange={setTermsOverride}
                    minRows={3}
                    maxHeight={220}
                    mediaMode="url-only"
                    placeholder="Payment, delivery, warranty, cancellation…"
                    testid="gst-composer-terms-override"
                  />
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    Rich text renders on the PDF. Keep clauses short — bullets scan better than prose.
                  </div>
                </div>
              )}
                </div>
                {/* Feb 2026 R293z · Cover thumbnail. Only renders when
                    cover is On (per-doc) or Default-On via workspace
                    profile; otherwise shows a muted "cover off" chip
                    so users can see what their choice means. */}
                <div className="min-w-0" data-testid="gst-composer-pdf-preview-wrap">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Cover preview</div>
                  {(() => {
                    const _coverKey = docType === "estimate" ? "quote" : "invoice";
                    const _effCover = includeCover === null
                      ? !!(bp && bp[`${_coverKey}_cover_enabled`])
                      : !!includeCover;
                    if (!_effCover) {
                      return (
                        <div
                          className="border border-dashed border-border bg-muted/30 rounded flex items-center justify-center text-[11px] text-muted-foreground text-center p-3"
                          style={{ aspectRatio: "210 / 297" }}
                          data-testid="gst-composer-cover-off"
                        >
                          Cover off — PDF starts on the main body
                        </div>
                      );
                    }
                    const _accent = (bp && bp[`${_coverKey}_cover_accent`]) || "#111827";
                    const _tag = coverTagline || (bp && bp[`${_coverKey}_cover_tagline`]) || "";
                    return (
                      <CoverThumb
                        accent={_accent}
                        tagline={_tag}
                        docType={docType}
                        subject={subject}
                        customer={customer?.name || customer?.customer_name}
                        docNo={isEdit ? editDoc?.doc_no : ""}
                        docDate={docDate}
                        wsName={bp?.legal_name || bp?.trade_name}
                      />
                    );
                  })()}
                  <a
                    href="/app/settings/billing-profile"
                    target="_blank" rel="noreferrer"
                    className="text-[10px] text-muted-foreground hover:text-foreground underline mt-1 block text-center"
                  >
                    Change cover defaults
                  </a>
                </div>
              </div>
            </Section>
          )}

          {/* Notes */}
          <Section
            icon={StickyNote}
            title="Notes & terms"
            hint="Shown on the PDF. Payment terms, delivery timeline, bank details — anything the customer needs to see."
            testid="gst-composer-section-notes"
            collapsible
            defaultOpen={false}
            badge={notes ? (
              <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                {notes.length}
              </span>
            ) : null}
          >
            <Textarea
              data-testid="gst-composer-notes"
              placeholder={docType === "estimate" ? "e.g. Valid for 30 days. 50% advance, 50% on delivery." : "Notes / terms (optional)"}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Section>

          {/* Feb 2026 R293zzb · Per-doc bank-account override. Only
              shows when the workspace has ≥2 bank accounts (otherwise
              the renderer's auto-pick is a non-choice anyway). */}
          {Array.isArray(profile?.bank_accounts) && profile.bank_accounts.length >= 2 && (
            <Section
              icon={Landmark}
              title="Payment account"
              hint={`Auto-picked by document currency (${currency}). Force a specific account here if you need the customer to pay into a different bank.`}
              testid="gst-composer-section-bank"
              collapsible
              defaultOpen={false}
              badge={bankAccountId ? (
                <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-200">Override</span>
              ) : null}
            >
              <Select
                value={bankAccountId || "__auto__"}
                onValueChange={(v) => setBankAccountId(v === "__auto__" ? "" : v)}
              >
                <SelectTrigger className="h-9 text-sm" data-testid="gst-composer-bank-account">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__">
                    Auto-pick by currency / default
                  </SelectItem>
                  {profile.bank_accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.label || a.bank_name || "Account"}{a.currency ? ` · ${a.currency}` : ""}{a.is_default ? " · default" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Feb 2026 R293zze · Flash a red hint when the picked
                  account's currency doesn't match the invoice. Catches
                  the "USD invoice printed with INR bank" mistake before
                  it leaves the Composer. */}
              {(() => {
                if (!bankAccountId) return null;
                const picked = profile.bank_accounts.find((a) => a.id === bankAccountId);
                if (!picked) return null;
                const docCcy = (currency || "").toUpperCase();
                const bankCcy = (picked.currency || "").toUpperCase();
                const defaults = (picked.default_for_currency || []).map((c) => String(c).toUpperCase());
                const ok = !bankCcy || !docCcy || bankCcy === docCcy || defaults.includes(docCcy);
                if (ok) return null;
                return (
                  <div className="mt-2 flex items-start gap-1.5 text-[11px] text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded px-2 py-1.5"
                       data-testid="gst-composer-bank-currency-mismatch">
                    <span className="inline-block w-1.5 h-1.5 bg-rose-500 rounded-full mt-1 flex-shrink-0 animate-pulse" />
                    <div>
                      <b>Currency mismatch</b> — document is in <b>{docCcy || "—"}</b> but
                      this account is set up for <b>{bankCcy || "—"}</b>. The PDF may
                      print routing details customers can't actually pay to.
                    </div>
                  </div>
                );
              })()}
            </Section>
          )}

          {/* Feb 2026 R293zze · Per-doc Payment Instructions — free text
              shown directly under the bank block on the preview + PDF.
              Use for intermediary-bank hints, reference numbers,
              installment plans, etc. */}
          <Section
            icon={Landmark}
            title="Payment instructions"
            hint="Optional free-text printed under the bank block. Reference numbers, intermediary bank hints, installment plans, etc."
            testid="gst-composer-section-pay-instructions"
            collapsible
            defaultOpen={false}
            badge={paymentInstructions ? (
              <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                set
              </span>
            ) : null}
          >
            <Textarea
              rows={3}
              placeholder="e.g. Please reference invoice INV-… in your wire. Our intermediary bank is Deutsche Bank, SWIFT DEUTUS33."
              value={paymentInstructions}
              onChange={(e) => setPaymentInstructions(e.target.value)}
              className="rounded-none text-sm"
              data-testid="gst-composer-payment-instructions"
            />
          </Section>

          {/* Feb 2026 R293zr · Attachments — opens up after the first save. */}
          <Section
            icon={Paperclip}
            title="Attachments"
            hint="Attach PDFs, images or spec sheets so the full brief travels with this document."
            testid="gst-composer-section-attachments"
            collapsible
            defaultOpen={false}
            badge={isEdit && (editDoc?.attachments || []).length ? (
              <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                {editDoc.attachments.length}
              </span>
            ) : null}
          >
            <AttachmentsPanel
              ws={ws}
              docId={isEdit ? editDoc?.id : null}
              initial={isEdit ? (editDoc?.attachments || []) : []}
            />
          </Section>

          {/* R268r · Revision note — only shown while editing an existing doc. */}
          {isEdit && (
            <div className="border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 rounded-lg p-3 space-y-1">
              <Label htmlFor="revision-note" className="text-xs uppercase tracking-widest text-amber-800 dark:text-amber-200">
                Reason for this edit
              </Label>
              <Textarea
                id="revision-note"
                data-testid="gst-composer-revision-note"
                placeholder={`Why are you editing ${editDoc.doc_no}? e.g. "Customer requested revised quantity"`}
                rows={2}
                value={revisionNote}
                onChange={(e) => setRevisionNote(e.target.value)}
              />
              <div className="text-[11px] text-amber-800 dark:text-amber-200">
                Saved to the revision history — visible in the document timeline.
              </div>
            </div>
          )}

          {/* Live preview panel — R268hh extracted to TotalsPanel */}
          <TotalsPanel
            preview={preview}
            tdsRate={tdsRate}
            currency={currency}
            homeCurrency={homeCurrency}
            fxRate={fxRate}
          />

          </div>{/* /inner */}
        </div>{/* /scrollable */}

        <DialogFooter className="bg-background border-t border-border px-4 py-2 gap-2 shrink-0 flex-row">
          <Button variant="outline" onClick={onClose} className="h-11 flex-1 sm:flex-none">Cancel</Button>
          <Button onClick={save} disabled={saving || !customer.name || !(lines[0]?.description)}
                    className="h-11 flex-1"
                    data-testid="gst-composer-save">
            {saving ? "Saving…" : isEdit
              ? <><CheckCircle2 className="h-4 w-4 mr-1" /> Save changes</>
              : recurringOn && docType === "invoice"
                ? <><Repeat className="h-4 w-4 mr-1" /> Save recurring template</>
                : <><CheckCircle2 className="h-4 w-4 mr-1" /> Create {_label(docType)}</>}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Feb 2026 R293zh · Standalone-Estimate picker (import mode). */}
      <EstimatePicker
        open={estimatePickerOpen}
        onClose={() => setEstimatePickerOpen(false)}
        ws={ws}
        mode="import"
        onPick={(payload) => {
          const picked = payload?._row || {};
          setSourceEstimateId(picked.id || null);
          setSourceEstimateNumber(picked.number || null);
          if (payload?.estimate?.title) setSubject((s) => s || payload.estimate.title);
          if (payload?.customer?.name && !customer.name) {
            setCustomer((c) => ({ ...c, ...payload.customer }));
          }
          if (Array.isArray(payload?.lines) && payload.lines.length) {
            setLines(payload.lines.map((l) => ({
              hsn_sac: l.hsn_sac || "",
              description: l.description || "",
              unit: l.unit || "Nos",
              qty: l.qty ?? 1,
              rate: l.rate ?? 0,
              discount_pct: l.discount_pct ?? 0,
              gst_rate: l.gst_rate ?? 18,
              cess_rate: l.cess_rate ?? 0,
            })));
          }
          toast.success(`Imported ${payload?.lines?.length || 0} line${payload?.lines?.length === 1 ? "" : "s"} from ${picked.number}`);
        }}
      />

      {/* Feb 2026 R293zq · Item catalog picker (multi-select) for lines. */}
      <CatalogDialog
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        ws={ws}
        onPickMany={addLinesFromCatalog}
      />
    </Dialog>
  );
}

